import * as THREE from 'three';

/**
 * AIM-9 Sidewinder — brick/pixel IR short-range AAM for the F-22 loadout.
 * White body, dark seeker, forward canards + rear fins, motor nozzle.
 * Local +Z is nose-forward (same as other projectiles).
 */
const BODY = 0xf0f2f4;
const BODY_SHADE = 0xd0d4d8;
const BAND = 0xc9a227;
const SEEKER = 0x1a1e24;
const SEEKER_GLASS = 0x2a3548;
const FIN = 0xe8eaee;
const FIN_EDGE = 0x9aa0a8;
const NOZZLE = 0x4a4f57;
const PLUME = 0xffd54f;
const PLUME_HOT = 0xfff59d;

/**
 * Compact Sidewinder silhouette. Collision radius on `userData.radius`.
 */
export function createSidewinder(): THREE.Group {
  const missile = new THREE.Group();
  missile.name = 'arcade-aim9-sidewinder';

  const bodyMat = new THREE.MeshBasicMaterial({ color: BODY });
  const shadeMat = new THREE.MeshBasicMaterial({ color: BODY_SHADE });
  const bandMat = new THREE.MeshBasicMaterial({ color: BAND });
  const seekerMat = new THREE.MeshBasicMaterial({ color: SEEKER });
  const glassMat = new THREE.MeshBasicMaterial({ color: SEEKER_GLASS });
  const finMat = new THREE.MeshBasicMaterial({ color: FIN });
  const edgeMat = new THREE.MeshBasicMaterial({ color: FIN_EDGE });
  const nozzleMat = new THREE.MeshBasicMaterial({ color: NOZZLE });
  const plumeMat = new THREE.MeshBasicMaterial({ color: PLUME, transparent: true, opacity: 0.9 });
  const plumeHotMat = new THREE.MeshBasicMaterial({
    color: PLUME_HOT,
    transparent: true,
    opacity: 0.95,
  });

  // Main tube (slightly longer/slimmer than brick bomb missile)
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.12, 1.35, 8), bodyMat);
  body.rotation.x = Math.PI / 2;
  body.position.z = 0.05;
  missile.add(body);

  // Mid-body shade ring + gold/ochre ID band (classic Sidewinder look)
  const mid = new THREE.Mesh(new THREE.CylinderGeometry(0.125, 0.125, 0.22, 8), shadeMat);
  mid.rotation.x = Math.PI / 2;
  mid.position.z = 0.1;
  missile.add(mid);
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.128, 0.128, 0.08, 8), bandMat);
  band.rotation.x = Math.PI / 2;
  band.position.z = 0.28;
  missile.add(band);

  // IR seeker head
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(0.11, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.55),
    glassMat,
  );
  dome.rotation.x = -Math.PI / 2;
  dome.position.z = 0.82;
  missile.add(dome);
  const seeker = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.11, 0.2, 8), seekerMat);
  seeker.rotation.x = Math.PI / 2;
  seeker.position.z = 0.72;
  missile.add(seeker);
  // Small tip brick for pixel read at distance
  const tip = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.12), glassMat);
  tip.position.z = 0.92;
  missile.add(tip);

  // Forward canards (4× cross, near seeker)
  const canardGeo = new THREE.BoxGeometry(0.06, 0.32, 0.18);
  for (let i = 0; i < 4; i++) {
    const canard = new THREE.Mesh(canardGeo, finMat);
    canard.rotation.z = (i * Math.PI) / 2;
    canard.position.z = 0.55;
    missile.add(canard);
  }
  // Canard root rings
  const canardRing = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.06, 8), edgeMat);
  canardRing.rotation.x = Math.PI / 2;
  canardRing.position.z = 0.55;
  missile.add(canardRing);

  // Rear fins / rollerons (4× larger, at motor)
  const finGeo = new THREE.BoxGeometry(0.07, 0.42, 0.28);
  for (let i = 0; i < 4; i++) {
    const fin = new THREE.Mesh(finGeo, finMat);
    fin.rotation.z = (i * Math.PI) / 2 + Math.PI / 4;
    fin.position.z = -0.45;
    missile.add(fin);
  }
  const finRoot = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.08, 8), edgeMat);
  finRoot.rotation.x = Math.PI / 2;
  finRoot.position.z = -0.45;
  missile.add(finRoot);

  // Rocket motor nozzle + plume
  const nozzle = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.11, 0.16, 8), nozzleMat);
  nozzle.rotation.x = Math.PI / 2;
  nozzle.position.z = -0.72;
  missile.add(nozzle);
  const plume = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.2), plumeMat);
  plume.position.z = -0.9;
  missile.add(plume);
  const plumeHot = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 0.14), plumeHotMat);
  plumeHot.position.z = -1.02;
  missile.add(plumeHot);

  missile.userData.radius = 0.8;
  missile.userData.missileKind = 'aim9';
  return missile;
}
